import { FunctionalComponent } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';

import { useCheckoutContext } from '@primer-io/shared-library/contexts';
import { PaymentMethodType } from '../enums/Tokens';
import { BasePaymentMethod } from '../payment-methods/BasePaymentMethod';
import { useModule, useDisableComponent } from '../utils/hooks';
import { getPaymentMethodUrl } from '../utils/ModuleResolver';
import { Scene } from '../enums/Checkout';
import { useWaitForComponent } from '../utils/SceneLoader/WaitForComponents';

type Props = {
  id: string;
  paymentMethod: BasePaymentMethod;
  onMounted?: () => void;
};

const ManagedPaymentMethodButton: FunctionalComponent<Props> = ({
  id,
  paymentMethod,
  onMounted,
}) => {
  const [mountError, setMountError] = useState(false);
  const rootRef = useRef<HTMLDivElement>();

  const { disableControls } = useDisableComponent({
    scene: Scene.CHOOSE_PAYMENT_METHOD,
  });
  const disablePaymentMethod = (disabled) => {
    paymentMethod.setDisabled(disabled);
  };

  useLayoutEffect(() => {
    (async () => {
      try {
        const result = await paymentMethod.mount();
        if (result === false) {
          setMountError(true);
        }
      } catch (e) {
        setMountError(true);
      }
      onMounted?.();
      disablePaymentMethod(disableControls);
    })();
  }, []);

  useLayoutEffect(() => {
    disablePaymentMethod(disableControls);
  }, [disableControls, paymentMethod]);

  if (mountError) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      id={`primer-checkout-apm-${id}`}
      class='PrimerCheckout__apmButton'
      tabindex='-1'
    ></div>
  );
};

const ModuleMethodButton: FunctionalComponent<Props> = ({
  id,
  paymentMethod,
  onMounted,
}) => {
  const { context } = useCheckoutContext();
  const url = context
    ? getPaymentMethodUrl(context, id as PaymentMethodType)
    : undefined;

  const { Module } = useModule<
    FunctionalComponent<{
      paymentMethod: BasePaymentMethod;
      onMounted?: () => void;
    }>
  >({
    remotePath: url ?? '',
    path: 'PaymentMethodButton',
    scope: id.toLowerCase(),
  });

  if (Module) {
    return <Module paymentMethod={paymentMethod} onMounted={onMounted} />;
  }

  return <div>Loading</div>;
};

const PaymentMethodButton: FunctionalComponent<Props> = ({
  id,
  paymentMethod,
}) => {
  const { wait, ready } = useWaitForComponent();

  useLayoutEffect(() => {
    wait(id);
  }, []);

  const handleMounted = () => {
    ready(id);
  };

  if (id === 'directDebit') {
    return (
      <ModuleMethodButton
        id={'gocardless'}
        paymentMethod={paymentMethod}
        onMounted={handleMounted}
      />
    );
  }

  if (id === 'klarna') {
    return (
      <ModuleMethodButton
        id={'klarna'}
        paymentMethod={paymentMethod}
        onMounted={handleMounted}
      />
    );
  }

  return (
    <ManagedPaymentMethodButton
      id={id}
      paymentMethod={paymentMethod}
      onMounted={handleMounted}
    />
  );
};

export default PaymentMethodButton;
